/**
 * 提示词与模型 · 页级草稿（PR6 新写，判定卡 08 差异 #2～#5、线框 04）。
 *
 * 改文本、换模型、重置为默认都只改这里的草稿；点页头「保存为新快照」才一次性走 A-32。
 * 放在模块级（不是组件 state）：页头按钮与页面主体共用一份；在 plan 里切到别的菜单再切回来，草稿还在。
 * 草稿第一次出现时钉住「基于哪个快照号」（base），保存时带上它，别人先存了后端回 409。
 */
import { useSyncExternalStore } from "react";

export type SaveIssue =
	| { kind: "conflict"; activeId: string }
	| { kind: "invalid"; items: { key: string; label: string; reason: string }[] }
	| { kind: "note"; labels: string[] }
	| { kind: "error"; message: string };

export type DraftState = {
	/** 配置项 key → 草稿值（提示词全文 ／ 模型 key）；和生效值相同的不留 */
	drafts: Record<string, string>;
	/** 草稿基于哪个快照号；没有草稿时为空 */
	base: string | null;
	note: string;
	saving: boolean;
	issue: SaveIssue | null;
	/** 「载入最新并保留我的修改」后，两边都改了的项（中文名），以我的为准 */
	overlap: string[];
};

const EMPTY: DraftState = {
	drafts: {},
	base: null,
	note: "",
	saving: false,
	issue: null,
	overlap: [],
};

let state: DraftState = EMPTY;
const listeners = new Set<() => void>();

function emit(next: DraftState) {
	state = next;
	for (const l of listeners) l();
}

export function getDraftState(): DraftState {
	return state;
}

export function patchDraftState(p: Partial<DraftState>) {
	emit({ ...state, ...p });
}

/** 改一项草稿：和生效值相同就撤掉；第一处草稿出现时钉住底稿快照号 */
export function setDraft(key: string, value: string, current: string, base: string | null) {
	const drafts = { ...state.drafts };
	if (value === current) delete drafts[key];
	else drafts[key] = value;
	if (!Object.keys(drafts).length) {
		emit({ ...EMPTY, note: state.note });
		return;
	}
	emit({ ...state, drafts, base: state.base ?? base, issue: keepIssue(state.issue, drafts) });
}

/** 某节点的草稿全撤掉后，挂在这个节点上的「校验不过」提示一起去掉（变量不齐挂在 user 段，但 system 段改动也会引起） */
function keepIssue(issue: SaveIssue | null, drafts: Record<string, string>): SaveIssue | null {
	if (issue?.kind !== "invalid") return issue;
	const keys = Object.keys(drafts);
	const items = issue.items.filter((i) => {
		const node = /^(plan\.prompt\.n\d)\./.exec(i.key)?.[1];
		return node ? keys.some((k) => k.startsWith(`${node}.`)) : i.key in drafts;
	});
	return items.length ? { ...issue, items } : null;
}

export function discardDraft(key: string) {
	const drafts = { ...state.drafts };
	delete drafts[key];
	if (!Object.keys(drafts).length) emit({ ...EMPTY, note: state.note });
	else emit({ ...state, drafts, issue: keepIssue(state.issue, drafts) });
}

export function discardAll() {
	emit(EMPTY);
}

/** 测试与保存成功后用：清空全部（含备注） */
export function resetDrafts() {
	emit(EMPTY);
}

function subscribe(l: () => void) {
	listeners.add(l);
	return () => listeners.delete(l);
}

export function useDraftState(): DraftState {
	return useSyncExternalStore(subscribe, getDraftState, getDraftState);
}
